import { validTextLength } from '@/environment';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getPageProperty, PolotnoStoreActivePageKeys, setPageProperty } from '@/pages/AdvancedEditor/hooks/polotno-store';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { ScriptType } from '@d-id/types';
import { reaction } from 'mobx';
import { useEffect } from 'react';
import { Page } from '../../AdvancedEditor';
import { updateSceneAudio } from '../../components/panels/script/ScriptPanel';
import { useCurrentProjectId } from '../useCurrentProjectId';
import { processSuccessfulUpload, useUploadAudio } from '../useUploadAudio';
import { getAudioResultUrl } from '../useUploadAudioFile';
import { InvalidTextLanguageError } from './InvalidTextLanguageError';
import { clearAudioCache, createTtsAudioWithRetry, getAudioListenUrl, getNewCacheVersion, getTtsPayload } from './scenes-preview';

export const MAX_CACHE_VERSION = 2000;

export const useScenesPreview = () => {
    const { store } = useAdvancedEditorContext();
    const missingScriptIndicatorEnabled = useFlag('missing_script_indicator_enabled', false);
    const { user } = useAuth();
    const projectId = useCurrentProjectId();
    const { mutateAsync: uploadAudio } = useUploadAudio();

    const createScenesTts = async () => {
        await Promise.all(
            store.pages
                .filter(page => getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript) === ScriptType.text)
                .filter(page => !getPageProperty(page, PolotnoStoreActivePageKeys.ttsCreated))
                .map(async page => {
                    const payload = getTtsPayload({ page, user, projectId });

                    const { duration, wordLevelTimestampsUrl, isEmpty } = await createTtsAudioWithRetry(payload);

                    clearAudioCache(page);

                    if (isEmpty) {
                        throw new InvalidTextLanguageError(page);
                    }

                    page.set({
                        duration: duration * 1000,
                        custom: {
                            ...page.custom,
                            [PolotnoStoreActivePageKeys.sceneDurationCalculated]: true,
                            [PolotnoStoreActivePageKeys.ttsCreated]: true,
                            [PolotnoStoreActivePageKeys.wordLevelTimestampsUrl]: wordLevelTimestampsUrl,
                            [PolotnoStoreActivePageKeys.ttsCacheVersion]: getNewCacheVersion(page),
                        },
                    });
                })
        );
    };

    const isSceneMissingScript = (page: Page) => {
        const isTextScene = getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript) === ScriptType.text;
        const textLength = getPageProperty(page, PolotnoStoreActivePageKeys.textInput).length;

        if (isTextScene) {
            return textLength < validTextLength;
        }

        const audioUploaded = getPageProperty(page, PolotnoStoreActivePageKeys.uploadedAudio);

        return !audioUploaded;
    };

    useEffect(() => {
        if (!missingScriptIndicatorEnabled) return;

        store.pages
            .filter(
                page =>
                    page.id !== store.activePage.id &&
                    !getPageProperty(page, PolotnoStoreActivePageKeys.shouldIndicateMissingScript) &&
                    isSceneMissingScript(page)
            )
            .forEach(page => {
                setPageProperty(page, PolotnoStoreActivePageKeys.shouldIndicateMissingScript)(true);
            });
    }, [store.activePage.id]);

    useEffect(() => {
        if (!missingScriptIndicatorEnabled) return;

        if (
            getPageProperty(store.activePage, PolotnoStoreActivePageKeys.shouldIndicateMissingScript) &&
            !isSceneMissingScript(store.activePage)
        ) {
            setPageProperty(store.activePage, PolotnoStoreActivePageKeys.shouldIndicateMissingScript)(false);
        }
    }, [
        getPageProperty(store.activePage, PolotnoStoreActivePageKeys.textInput),
        getPageProperty(store.activePage, PolotnoStoreActivePageKeys.uploadedAudio),
        getPageProperty(store.activePage, PolotnoStoreActivePageKeys.selectedScript),
    ]);

    const validateScenesNotEmpty = () => {
        const areAllScenesNotEmpty = store.pages.every(page => !isSceneMissingScript(page));

        if (
            missingScriptIndicatorEnabled &&
            !getPageProperty(store.activePage, PolotnoStoreActivePageKeys.shouldIndicateMissingScript) &&
            isSceneMissingScript(store.activePage)
        ) {
            setPageProperty(store.activePage, PolotnoStoreActivePageKeys.shouldIndicateMissingScript)(true);
        }

        return areAllScenesNotEmpty;
    };

    const allTtsCreated = store.pages
        .filter(page => getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript) === ScriptType.text)
        .every(page => getPageProperty(page, PolotnoStoreActivePageKeys.ttsCreated));

    const audioLoaded = store.pages.every(page => !!getPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio));
    const sceneShouldIndicateMissingScripts = store.pages.map(page =>
        getPageProperty(page, PolotnoStoreActivePageKeys.shouldIndicateMissingScript)
    );

    const preloadAudio = async () => {
        await Promise.all(
            store.pages
                .filter(page => !getPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio))
                .map(async page => {
                    const listenUrl = getAudioListenUrl({ projectId, page, user });
                    const response = await fetch(listenUrl, { cache: 'force-cache' });

                    if (!response.ok) {
                        logger.error(`Failed to load scene audio`, {
                            listenUrl,
                            response: await response.text(),
                            scriptType: getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript),
                        });

                        throw new Error(`Failed to load audio for page ${page.id}`);
                    }

                    const audioBlob = await response.blob();
                    const audioUrl = URL.createObjectURL(audioBlob);

                    setPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio)(audioUrl);
                })
        );
    };

    // Clear audio cache when page is removed
    useEffect(() => {
        const dispose = reaction(
            () => ({
                numberOfPages: store.pages.length,
                cachedAudios: store.pages.map(page => getPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio)).filter(Boolean),
            }),
            (curr, prev) => {
                if (curr.numberOfPages >= prev.numberOfPages) {
                    return;
                }

                const removedCachedAudios = prev.cachedAudios.filter((audio: string) => !curr.cachedAudios.includes(audio)).filter(Boolean);

                removedCachedAudios.forEach(URL.revokeObjectURL);
            }
        );

        return () => {
            dispose();
        };
    }, [store.pages.length]);

    // Clear audio cache when page is added/duplicated
    // Also, upload audio for duplicated page if the original page has audio
    useEffect(() => {
        const dispose = reaction(
            () => store.pages.map(page => page.id),
            async (ids, prevIds) => {
                if (ids.length <= prevIds.length) {
                    return;
                }

                const newPage = store.pages.find(page => !prevIds.includes(page.id));

                if (!newPage) {
                    return;
                }

                setPageProperty(newPage, PolotnoStoreActivePageKeys.cachedAudio)('');
                const hasAudio =
                    getPageProperty(newPage, PolotnoStoreActivePageKeys.selectedScript) !== ScriptType.text &&
                    !!getPageProperty(newPage, PolotnoStoreActivePageKeys.uploadedAudio)?.duration;

                if (hasAudio) {
                    try {
                        const resultUrl = getAudioResultUrl({
                            user,
                            projectId,
                            pageId: newPage.id,
                        });

                        const { url, duration } = await uploadAudio({
                            sourceUrl: getPageProperty(newPage, PolotnoStoreActivePageKeys.uploadedAudio)?.data as string,
                            resultUrl,
                            persist: true,
                        });

                        const { data, listenUrl, name } = processSuccessfulUpload(url, duration, resultUrl);
                        updateSceneAudio({ page: newPage, audioData: { data, listenUrl, name, duration } });
                    } catch (e) {
                        logger.error('Failed to upload audio for duplicated page', e);

                        updateSceneAudio({ page: newPage, audioData: null });
                    }
                }

                newPage.set({
                    custom: {
                        ...newPage.custom,
                        [PolotnoStoreActivePageKeys.ttsCreated]: false,
                        [PolotnoStoreActivePageKeys.wordLevelTimestampsUrl]: undefined,
                        ...(!hasAudio && {
                            [PolotnoStoreActivePageKeys.sceneDurationCalculated]: false,
                            [PolotnoStoreActivePageKeys.ttsCacheVersion]: 0,
                        }),
                    },
                });
            }
        );

        return () => {
            dispose();
        };
    }, [store.pages.length]);

    // For pages with script only: Clear audio cache when voice config changes on the active page, not on switching page
    useEffect(() => {
        const dispose = reaction(
            () => ({
                voiceConfig: getPageProperty(store.activePage, PolotnoStoreActivePageKeys.ttsVoiceConfig),
                activePageId: store.activePage?.id,
            }),
            ({ voiceConfig, activePageId }, prev) => {
                if (
                    !store.activePage?.id ||
                    voiceConfig === prev.voiceConfig ||
                    activePageId !== prev.activePageId ||
                    getPageProperty(store.activePage, PolotnoStoreActivePageKeys.selectedScript) !== ScriptType.text
                ) {
                    return;
                }

                setPageProperty(store.activePage, PolotnoStoreActivePageKeys.cachedAudio)('');

                store.activePage.set({
                    custom: {
                        ...store.activePage.custom,
                        [PolotnoStoreActivePageKeys.ttsCreated]: false,
                        [PolotnoStoreActivePageKeys.wordLevelTimestampsUrl]: undefined,
                        [PolotnoStoreActivePageKeys.sceneDurationCalculated]: false,
                    },
                });
            }
        );

        return () => {
            dispose();
        };
    }, [store.activePage]);

    const preparePreview = async () => {
        if (!allTtsCreated) {
            await createScenesTts();
        }

        await preloadAudio();
    };

    const previewReady = allTtsCreated && audioLoaded;

    return { preparePreview, previewReady, validateScenesNotEmpty, sceneShouldIndicateMissingScripts };
};
